/**
 * Carrying a rehearsed launch from devnet to mainnet.
 *
 * The point is not to save typing. It is to make "this is what I tested" a
 * checkable claim instead of a memory. A devnet launch produces a record of
 * what the CHAIN said afterwards — not what the form said before — and loading
 * that record on mainnet fills the identity fields and then keeps comparing.
 *
 * Three things this file refuses to carry, each because carrying it fails
 * quietly rather than loudly:
 *
 *   - The fee tier account. It is a cluster-specific address, and index N is a
 *     different rate on each cluster (devnet index 1 is 0.30%, mainnet index 1
 *     is 1.00%). A carried address either does not exist on mainnet or, worse,
 *     resolves to a tier you did not test. The rehearsed RATE is carried
 *     instead, as something to compare against.
 *   - The recipient list. A devnet distribution is a list of throwaway wallets.
 *     Sending real supply to addresses nobody controls cannot be undone.
 *   - The metadata URI. A rehearsal is usually run against a placeholder, and a
 *     placeholder that ships to mainnet under immutable metadata is permanent.
 *
 * Everything stripped is reported with its reason, so the operator sees what
 * they still have to decide rather than discovering it at the wallet prompt.
 */

export const REHEARSAL_VERSION = 1;

export interface RehearsalPlan {
  name: string;
  symbol: string;
  decimals: number;
  totalSupply: string;
  mutableMetadata: boolean;
  revokeMint: boolean;
  revokeFreeze: boolean;
  openPool: boolean;
  tokenLiquidity: string;
  solLiquidity: string;
}

/** What the chain said after the rehearsal, read back rather than assumed. */
export interface RehearsalObserved {
  mint: string;
  supplyRaw: string;
  decimals: number;
  mintAuthority: string;
  freezeAuthority: string;
  metadataName: string;
  metadataSymbol: string;
  metadataMutable: boolean;
  poolId?: string;
  lpMint?: string;
  /** The rate itself, never the cluster-specific config address. */
  tradeFeeRate?: number;
  creatorFeeRate?: number;
  openTimeUnix?: number;
  tokenReserveRaw?: string;
  solReserveRaw?: string;
  openingPrice?: string;
}

export interface RehearsalRecord {
  version: number;
  createdAt: string;
  cluster: string;
  rpcUrl?: string;
  plan: RehearsalPlan;
  observed: RehearsalObserved;
}

export interface StrippedField {
  field: string;
  why: string;
}

export interface CarryResult {
  plan: RehearsalPlan;
  stripped: StrippedField[];
  notes: string[];
}

export type DiffSeverity = "blocking" | "warn";

export interface PlanDifference {
  field: string;
  rehearsed: string;
  current: string;
  severity: DiffSeverity;
  why: string;
}

export function buildRecord(args: {
  cluster: string;
  rpcUrl?: string;
  plan: RehearsalPlan;
  observed: RehearsalObserved;
  now?: Date;
}): RehearsalRecord {
  return {
    version: REHEARSAL_VERSION,
    createdAt: (args.now ?? new Date()).toISOString(),
    cluster: args.cluster,
    rpcUrl: args.rpcUrl,
    plan: { ...args.plan },
    observed: { ...args.observed },
  };
}

/** Parse a record, refusing anything that is not one rather than half-reading it. */
export function parseRecord(text: string): { record: RehearsalRecord } | { error: string } {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (e) {
    return { error: `Not valid JSON: ${(e as Error).message}` };
  }
  const r = raw as Partial<RehearsalRecord>;
  if (!r || typeof r !== "object") return { error: "Not a rehearsal record." };
  if (r.version !== REHEARSAL_VERSION) {
    return {
      error: `Record version ${String(r.version)} is not supported (expected ${REHEARSAL_VERSION}).`,
    };
  }
  if (!r.plan || !r.observed || typeof r.cluster !== "string") {
    return { error: "Record is missing its plan, observations or cluster." };
  }
  const p = r.plan as Partial<RehearsalPlan>;
  for (const key of ["name", "symbol", "totalSupply", "tokenLiquidity", "solLiquidity"] as const) {
    if (typeof p[key] !== "string") return { error: `Record plan.${key} is missing or not a string.` };
  }
  if (typeof p.decimals !== "number") return { error: "Record plan.decimals is missing." };
  const o = r.observed as Partial<RehearsalObserved>;
  if (typeof o.mint !== "string" || typeof o.supplyRaw !== "string") {
    return { error: "Record observed.mint or observed.supplyRaw is missing." };
  }
  return { record: r as RehearsalRecord };
}

/**
 * What survives the trip to another cluster, and what does not.
 *
 * Liquidity amounts DO carry, deliberately, but with a note: they are the one
 * carried pair whose meaning changes completely when the SOL is real.
 */
export function carryToCluster(record: RehearsalRecord, targetCluster: string): CarryResult {
  const stripped: StrippedField[] = [
    {
      field: "Fee tier",
      why:
        "The AMM config account is cluster-specific and index N is a different rate on each " +
        `cluster. Choose the tier again on ${targetCluster} and check the rate against the ` +
        (record.observed.tradeFeeRate !== undefined
          ? `${(record.observed.tradeFeeRate / 10_000).toFixed(4).replace(/0+$/, "").replace(/\.$/, "")}% you rehearsed.`
          : "one you rehearsed."),
    },
    {
      field: "Metadata JSON URI",
      why:
        "A rehearsal is usually run against a placeholder. A placeholder shipped under immutable " +
        "metadata is permanent, so the URI is always re-entered and re-tested.",
    },
    {
      field: "Recipient list",
      why:
        "A devnet distribution is a list of throwaway wallets. Import the real CSV on the " +
        "Distribute tab; sending supply to addresses nobody controls cannot be undone.",
    },
    {
      field: "Trading open time",
      why: "The rehearsed time is in the past. Choose the real launch window.",
    },
    {
      field: "RPC endpoint",
      why: "A devnet endpoint on a mainnet launch is refused. Use a provider you trust.",
    },
    {
      field: "Mint and pool addresses",
      why: `They belong to the ${record.cluster} rehearsal. A new launch creates new ones.`,
    },
  ];

  const notes: string[] = [];
  if (record.plan.openPool) {
    notes.push(
      `Liquidity amounts carried over: ${record.plan.tokenLiquidity} tokens and ` +
        `${record.plan.solLiquidity} SOL. On ${record.cluster} that SOL was free. Decide the ` +
        "number again as an amount of real money."
    );
  }
  if (record.observed.mintAuthority !== "null" || record.observed.freezeAuthority !== "null") {
    notes.push(
      "The rehearsal finished with at least one authority still held. That is a real choice, " +
        "but confirm it is the one you meant to make with a live token."
    );
  }
  if (!record.plan.mutableMetadata) {
    notes.push(
      "Metadata was frozen in the rehearsal. On a live token that makes the name, symbol and " +
        "URI permanent — check the URI resolves before you repeat it."
    );
  }

  return { plan: { ...record.plan }, stripped, notes };
}

/**
 * Compare what is about to be launched with what was actually rehearsed.
 *
 * Identity and authority policy are `blocking`: those define the token, and a
 * difference means the thing being launched is not the thing that was tested.
 * Economics are `warn`: changing them on purpose is normal.
 */
export function diffAgainstRecord(record: RehearsalRecord, current: RehearsalPlan): PlanDifference[] {
  const out: PlanDifference[] = [];
  const add = (
    field: string,
    rehearsed: unknown,
    cur: unknown,
    severity: DiffSeverity,
    why: string
  ) => {
    if (String(rehearsed) !== String(cur)) {
      out.push({ field, rehearsed: String(rehearsed), current: String(cur), severity, why });
    }
  };

  add("Token name", record.plan.name, current.name, "blocking", "Written into the metadata account and shown everywhere.");
  add("Symbol", record.plan.symbol, current.symbol, "blocking", "What wallets and explorers display.");
  add("Decimals", record.plan.decimals, current.decimals, "blocking", "Fixed at creation; changes every amount you tested.");
  add("Total supply", record.plan.totalSupply, current.totalSupply, "blocking", "Minted once and, after revocation, forever.");
  add("Revoke mint authority", record.plan.revokeMint, current.revokeMint, "blocking", "Decides whether the supply can ever grow.");
  add("Revoke freeze authority", record.plan.revokeFreeze, current.revokeFreeze, "blocking", "Decides whether holder accounts can be frozen.");
  add("Metadata mutable", record.plan.mutableMetadata, current.mutableMetadata, "blocking", "Decides whether a dead URI can ever be fixed.");
  add("Open a pool", record.plan.openPool, current.openPool, "blocking", "A token with no pool is not tradeable.");

  add(
    "Tokens into the pool",
    record.plan.tokenLiquidity,
    current.tokenLiquidity,
    "warn",
    "Changes the opening price and the share of supply that is tradeable."
  );
  add(
    "SOL into the pool",
    record.plan.solLiquidity,
    current.solLiquidity,
    "warn",
    "Changes the opening price, and here it is real money."
  );

  return out;
}

export function blockingDifferences(diffs: PlanDifference[]): PlanDifference[] {
  return diffs.filter((d) => d.severity === "blocking");
}

/** A filename that identifies the run without needing to be opened. */
export function recordFilename(record: RehearsalRecord): string {
  const stamp = record.createdAt.replace(/[:.]/g, "-").replace(/Z$/, "");
  const symbol = record.plan.symbol.replace(/[^A-Za-z0-9]/g, "") || "token";
  return `rehearsal-${symbol}-${record.cluster}-${stamp}.json`;
}
